/**
 * @fileoverview NetAssetsChart - Grouped bars + line for net assets analysis
 *
 * Purpose: Display total assets, liabilities, and net assets per period.
 * Features polished styling: rounded bars, subtle grid, styled line dots.
 *
 * Used by: Review phase balance sheet analysis
 */

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import {
  periodAlignedLayout,
  type PeriodAlignedChartProps,
} from "./periodAlignedLayout";

const config = {
  totalAssets: { label: "Total Assets", color: "hsl(var(--chart-1))" },
  totalLiabilities: {
    label: "Total Liabilities",
    color: "hsl(var(--chart-3))",
  },
  netAssets: { label: "Net Assets", color: "hsl(var(--chart-2))" },
} satisfies ChartConfig;

const dollarFormatter = (v: number) => `$${(v / 1000).toFixed(0)}k`;

export interface NetAssetsChartProps extends PeriodAlignedChartProps {
  periods: string[];
  totalAssets: (number | null)[];
  totalLiabilities: (number | null)[];
  netAssets: (number | null)[];
}

/** Grouped asset and liability bars per period with a net assets line. */
export function NetAssetsChart({
  periods,
  totalAssets,
  totalLiabilities,
  netAssets,
  plotInsetLeft,
}: NetAssetsChartProps) {
  const layout = periodAlignedLayout(plotInsetLeft, 60);
  // Missing values stay null — a bar is skipped and a line breaks — rather
  // than plotting as zero beside a table that shows a dash (MOR-1282 QA).
  const data = periods.map((period, i) => ({
    period,
    totalAssets: totalAssets[i] ?? null,
    totalLiabilities: totalLiabilities[i] ?? null,
    netAssets: netAssets[i] ?? null,
  }));

  return (
    <ChartContainer config={config} className="min-h-[180px] w-full">
      <ComposedChart data={data} margin={layout.margin}>
        <CartesianGrid
          vertical={false}
          stroke="hsl(var(--border))"
          strokeOpacity={0.5}
        />
        <XAxis
          dataKey="period"
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tickFormatter={dollarFormatter}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
          width={layout.yAxisWidth}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(v) =>
                typeof v === "number" ? dollarFormatter(v) : "—"
              }
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar
          dataKey="totalAssets"
          fill="hsl(var(--chart-1))"
          radius={[4, 4, 0, 0]}
        />
        <Bar
          dataKey="totalLiabilities"
          fill="hsl(var(--chart-3))"
          radius={[4, 4, 0, 0]}
        />
        <Line
          type="monotone"
          dataKey="netAssets"
          stroke="hsl(var(--chart-2))"
          strokeWidth={2}
          dot={{
            r: 3,
            fill: "hsl(var(--chart-2))",
            strokeWidth: 2,
            stroke: "hsl(var(--background))",
          }}
          activeDot={{ r: 5, strokeWidth: 2 }}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
